// client/src/views/Interactive_parts/shapeHandlers.js
export function createShapeHandlers({
  apiBase,
  isMapOwnerOrFounder,
  selectedShape,
  selectedLayer,
  setSelectedShape,
  setSelectedLayer,
  setSavingShape,
  setPathingEntries,
  onZonesChanged,
  shapeLayerMap,
}) {
  function openShapeToolbar(shapeToOpen, layer) {
    if (!isMapOwnerOrFounder) return;
    const stroke =
      (layer && layer.options && layer.options.color) ??
      shapeToOpen.strokeColor;
    const fill =
      (layer && layer.options && layer.options.fillColor) ??
      shapeToOpen.fillColor;

    const merged = {
      ...shapeToOpen,
      strokeColor: stroke ?? shapeToOpen.strokeColor,
      fillColor: fill ?? shapeToOpen.fillColor,
      strokeOpacity:
        (layer && layer.options && layer.options.opacity) ??
        shapeToOpen.strokeOpacity,
      fillOpacity:
        (layer && layer.options && layer.options.fillOpacity) ??
        shapeToOpen.fillOpacity,
      tokenColor:
        shapeToOpen.tokenColor ||
        shapeToOpen.token_color ||
        stroke ||
        fill,
    };

    setSelectedShape(merged);
    setSelectedLayer(
      layer || (shapeToOpen && shapeLayerMap.current[shapeToOpen.id]) || null
    );
  }

  function handleGeomanButtonClick(btnName) {
    if (!isMapOwnerOrFounder) return;
    if (btnName !== 'Text') {
      if (selectedShape && selectedLayer) {
        openShapeToolbar(selectedShape, selectedLayer);
      } else {
        setSelectedShape(null);
        setSelectedLayer(null);
      }
    }
  }

  async function handleSaveShape(shapeToSave) {
    setSavingShape(true);
    let geometry = shapeToSave.geometry;
    let radius = shapeToSave.radius;
    let shapeType = shapeToSave.shapeType;

    if (selectedLayer) {
      if (typeof selectedLayer.getRadius === 'function') {
        const center = selectedLayer.getLatLng();
        geometry = {
          type: 'Point',
          coordinates: [center.lng, center.lat],
        };
        radius = selectedLayer.getRadius();
        shapeType = 'Circle';
      } else {
        geometry = selectedLayer.toGeoJSON().geometry;
        shapeType = geometry.type || '';
        radius = null;
      }
      if (selectedLayer.pm && selectedLayer.pm.enabled()) {
        selectedLayer.pm.disable();
      }
      selectedLayer.setStyle({
        color: shapeToSave.strokeColor,
        fillColor: shapeToSave.fillColor,
        opacity: shapeToSave.strokeOpacity,
        fillOpacity: shapeToSave.fillOpacity,
      });
      selectedLayer.options.color = shapeToSave.strokeColor;
      selectedLayer.options.fillColor = shapeToSave.fillColor;
      selectedLayer.options.opacity = shapeToSave.strokeOpacity;
      selectedLayer.options.fillOpacity = shapeToSave.fillOpacity;
      if (shapeToSave.label !== undefined) {
        selectedLayer.options.label = shapeToSave.label;
      }
    }

    const resolvedBehaviorType =
      shapeToSave.behaviorType ?? shapeToSave.behavior_type ?? null;
    const resolvedBehaviorProps =
      shapeToSave.behaviorProps ?? shapeToSave.behavior_props ?? undefined;
    const resolvedBehaviorState =
      shapeToSave.behaviorState ?? shapeToSave.behavior_state ?? undefined;

    const payload = {
      id: shapeToSave.id,
      geometry,
      radius,
      shapeType,
      fillColor: shapeToSave.fillColor,
      strokeColor: shapeToSave.strokeColor,
      strokeOpacity: shapeToSave.strokeOpacity,
      fillOpacity: shapeToSave.fillOpacity,
      label: shapeToSave.label,
      pattern: shapeToSave.pattern,
      behaviorType: resolvedBehaviorType,
      behavior_type: resolvedBehaviorType,
      behaviorProps: resolvedBehaviorProps,
      behavior_props: resolvedBehaviorProps,
      behaviorState: resolvedBehaviorState,
      behavior_state: resolvedBehaviorState,
      sprite: shapeToSave.sprite ?? undefined,
      tokenIconUrl: shapeToSave.tokenIconUrl ?? null,
      tokenColor:
        shapeToSave.tokenColor ||
        shapeToSave.strokeColor ||
        shapeToSave.fillColor ||
        null,
      tokenName: shapeToSave.tokenName ?? null,
      tokenSpeed:
        typeof shapeToSave.tokenSpeed === 'number'
          ? shapeToSave.tokenSpeed
          : 0.25,
      tokenDirection: shapeToSave.tokenDirection || 'forward',
      tokenDefaultPosition:
        typeof shapeToSave.tokenDefaultPosition === 'number'
          ? shapeToSave.tokenDefaultPosition
          : 0.0,
      tokenAutoMove:
        shapeToSave.tokenAutoMove === 1 ||
        shapeToSave.tokenAutoMove === true
          ? 1
          : 0,
    };

    if (selectedLayer) {
      selectedLayer._shapeMeta = {
        ...(selectedLayer._shapeMeta || {}),
        ...payload,
      };
    }

    try {
      await fetch(`${apiBase}/${shapeToSave.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setSelectedShape(prev =>
        prev ? { ...prev, ...payload } : { ...payload }
      );

      setPathingEntries(prev =>
        Array.isArray(prev)
          ? prev.map(entry =>
              entry.id === shapeToSave.id
                ? {
                    ...entry,
                    shape: {
                      ...entry.shape,
                      ...payload,
                    },
                  }
                : entry
            )
          : prev
      );

      if (onZonesChanged) {
        onZonesChanged();
      }
    } catch {
    }
    setSavingShape(false);
    setSelectedLayer(null);
  }

  async function handleDeleteShape(shapeToDelete, layer) {
    try {
      await fetch(`${apiBase}/${shapeToDelete.id}`, { method: 'DELETE' });
      if (layer && layer.remove) layer.remove();
      if (layer && layer._map && layer._map.removeLayer) {
        layer._map.removeLayer(layer);
      }
      setSelectedShape(null);
      setSelectedLayer(null);

      setPathingEntries(prev =>
        Array.isArray(prev)
          ? prev.filter(entry => entry.id !== shapeToDelete.id)
          : prev
      );

      if (onZonesChanged) {
        onZonesChanged();
      }
    } catch {
    }
  }

  function handleShapeToolbarChange(newShape) {
    const merged = { ...newShape };
    if (!merged.tokenColor) {
      merged.tokenColor =
        merged.strokeColor ||
        merged.fillColor ||
        (selectedLayer ? selectedLayer.options.color : null) ||
        merged.tokenColor;
    }

    setSelectedShape(merged);
    if (selectedLayer) {
      selectedLayer.setStyle({
        color: merged.strokeColor,
        fillColor: merged.fillColor,
        opacity: merged.strokeOpacity,
        fillOpacity: merged.fillOpacity,
      });
      selectedLayer.options.color = merged.strokeColor;
      selectedLayer.options.fillColor = merged.fillColor;
      selectedLayer.options.opacity = merged.strokeOpacity;
      selectedLayer.options.fillOpacity = merged.fillOpacity;
      if (merged.label !== undefined) {
        selectedLayer.options.label = merged.label;
      }
      if (selectedLayer._shapeMeta) {
        selectedLayer._shapeMeta = {
          ...selectedLayer._shapeMeta,
          ...merged,
        };
      }
    }

    setPathingEntries(prev =>
      Array.isArray(prev)
        ? prev.map(entry =>
            entry.id === merged.id
              ? {
                  ...entry,
                  shape: {
                    ...entry.shape,
                    ...merged,
                  },
                }
              : entry
          )
        : prev
    );
  }

  return {
    handleGeomanButtonClick,
    handleSaveShape,
    handleDeleteShape,
    handleShapeToolbarChange,
    openShapeToolbar,
  };
}
